import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { ArrowUp, ArrowDown, RefreshCw, Star, Trash2, Plus, ImageOff } from 'lucide-react'
import type { ContentItem, MockupSlide, MockupArtwork } from '../../api/contentApi'
import { useGenerateMockupSlot } from '../../hooks/useContent'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>

interface MockupEditorProps {
  item: ContentItem
  onSave: (payload: Payload) => void
  saving?: boolean
}

// Curated room catalogue (mirrors ams/lib/content/mockup/rooms.json) for add/regenerate.
const ROOMS: { id: string; mood: string }[] = [
  { id: 'quiet-earth', mood: 'Grounding' }, { id: 'golden-hour', mood: 'Radiant' },
  { id: 'ember-glow', mood: 'Intimate' }, { id: 'soft-clay', mood: 'Tender' },
  { id: 'slow-linen', mood: 'Serene' }, { id: 'coastal-calm', mood: 'Breezy' },
  { id: 'still-water', mood: 'Contemplative' }, { id: 'soft-stone', mood: 'Grounding' },
  { id: 'salt-air', mood: 'Relaxed' }, { id: 'iron-moss', mood: 'Grounding' },
  { id: 'studio-noir', mood: 'Bold' }, { id: 'clean-edge', mood: 'Focused' },
  { id: 'midnight-velvet', mood: 'Luxurious' }, { id: 'raw-mark', mood: 'Energizing' },
  { id: 'wild-edge', mood: 'Bold' }, { id: 'morning-glass', mood: 'Hopeful' },
  { id: 'gentle-bloom', mood: 'Tender' }, { id: 'soft-grid', mood: 'Playful' },
  { id: 'bright-signal', mood: 'Energizing' }, { id: 'warm-current', mood: 'Flowing' },
  { id: 'desk-view', mood: 'Focused' }, { id: 'first-light', mood: 'Serene' },
  { id: 'grand-entry', mood: 'Welcoming' }, { id: 'line-story', mood: 'Intimate' },
  { id: 'far-field', mood: 'Expansive' }, { id: 'woven-thread', mood: 'Crafted' },
  { id: 'open-window', mood: 'Wandering' }, { id: 'frost-line', mood: 'Crisp' },
  { id: 'sun-print', mood: 'Joyful' }, { id: 'good-night', mood: 'Gentle' },
]

export function MockupEditor({ item, onSave, saving }: MockupEditorProps) {
  const payload = (item.payload as Payload) || {}
  const source = (payload.source as Payload) || {}
  const artwork: MockupArtwork = source.artwork || {}
  const style = source.style || { kind: 'brand' }

  const [slides, setSlides] = useState<MockupSlide[]>(() => (payload.slides as MockupSlide[]) || [])
  const [addRoom, setAddRoom] = useState<string>('ember-glow')
  const [busyIdx, setBusyIdx] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)
  const slot = useGenerateMockupSlot()

  useEffect(() => {
    setSlides(((item.payload as Payload)?.slides as MockupSlide[]) || [])
  }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (next: MockupSlide[]) => {
    setSlides(next)
    onSave({ ...payload, slides: next })
  }

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= slides.length) return
    const next = slides.slice()
    ;[next[i], next[j]] = [next[j], next[i]]
    commit(next)
  }
  const makePrimary = (i: number) => {
    const next = slides.slice()
    const [s] = next.splice(i, 1)
    next.unshift(s)
    commit(next)
  }
  const toggleAccept = (i: number) => {
    const next = slides.slice()
    next[i] = { ...next[i], accepted: next[i].accepted === false }
    commit(next)
  }
  const remove = (i: number) => commit(slides.filter((_, k) => k !== i))

  const regenerate = async (i: number) => {
    const s = slides[i]
    setBusyIdx(i)
    try {
      const fresh = await slot.mutateAsync({
        artwork,
        slot: { kind: s.kind, room: s.room || undefined, aspect: s.kind === 'template' ? '4:5' : '4:3' },
        style,
      })
      const next = slides.slice()
      next[i] = { ...fresh, accepted: s.accepted }
      commit(next)
    } finally {
      setBusyIdx(null)
    }
  }

  const addScene = async () => {
    setAdding(true)
    try {
      const fresh = await slot.mutateAsync({ artwork, slot: { kind: 'scene', room: addRoom, aspect: '4:3' }, style })
      commit([...slides, fresh])
    } finally {
      setAdding(false)
    }
  }

  const accepted = slides.filter((s) => s.accepted !== false).length
  const hasPrimary = slides.length > 0 && slides[0].kind === 'template' && slides[0].accepted !== false

  return (
    <div className="space-y-5">
      {/* Source + summary */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {artwork.cdnId || artwork.url ? (
            <img
              src={artwork.url || `https://picasso.arthaus.cloud/cache/artworks/${artwork.cdnId}.webp`}
              alt={artwork.title || ''}
              className="w-12 h-12 rounded object-cover border"
            />
          ) : null}
          <div className="text-sm">
            <div className="font-medium">{artwork.title || item.targetRef}</div>
            <div className="text-muted-foreground text-xs">
              {accepted}/{slides.length} slides · style: {String(style.kind || 'brand')} · → product <code>{item.targetRef}</code>
            </div>
          </div>
        </div>
      </div>

      {!hasPrimary && slides.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Slide 1 should be a <strong>template</strong> (the faithful, variant-driven primary). Use ★ to promote one to the front.
        </p>
      )}

      {/* Slide grid */}
      {slides.length === 0 ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2 py-8 justify-center">
          <ImageOff className="h-4 w-4" /> No slides yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {slides.map((s, i) => {
            const dropped = s.accepted === false
            return (
              <div key={`${s.url}-${i}`} className={`rounded-lg border overflow-hidden ${dropped ? 'opacity-40' : ''} ${i === 0 ? 'ring-2 ring-primary' : ''}`}>
                <div className="relative bg-muted aspect-[4/3]">
                  <img src={`${s.url}${s.url.includes('?') ? '&' : '?'}width=600`} alt={s.alt || ''} className="w-full h-full object-cover" />
                  <span className="absolute top-1 left-1 text-[10px] uppercase tracking-wide bg-charcoal/80 text-white px-1.5 py-0.5 rounded">
                    {s.kind === 'template' ? (i === 0 ? 'Primary' : 'Template') : s.mood || 'Scene'}
                  </span>
                  {busyIdx === i && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-xs">
                      <RefreshCw className="h-4 w-4 animate-spin mr-1" /> regenerating…
                    </div>
                  )}
                </div>
                <div className="p-2 space-y-1">
                  <div className="text-[11px] text-muted-foreground truncate">{s.room || 'studio'}{s.width ? ` · ${s.width}×${s.height}` : ''}</div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <IconBtn title="Move left" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="h-3.5 w-3.5 -rotate-90" /></IconBtn>
                    <IconBtn title="Move right" onClick={() => move(i, 1)} disabled={i === slides.length - 1}><ArrowDown className="h-3.5 w-3.5 -rotate-90" /></IconBtn>
                    <IconBtn title="Make primary" onClick={() => makePrimary(i)} disabled={i === 0}><Star className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn title="Regenerate" onClick={() => regenerate(i)} disabled={busyIdx !== null}><RefreshCw className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn title={dropped ? 'Include' : 'Exclude'} onClick={() => toggleAccept(i)}>
                      <span className="text-[11px] font-medium px-1">{dropped ? 'Off' : 'On'}</span>
                    </IconBtn>
                    <IconBtn title="Remove" onClick={() => remove(i)}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Add a scene */}
      <div className="flex items-end gap-2 border-t pt-4">
        <div className="space-y-1">
          <Label className="text-xs">Add a room scene</Label>
          <select
            value={addRoom}
            onChange={(e) => setAddRoom(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {ROOMS.map((r) => (
              <option key={r.id} value={r.id}>{r.id} · {r.mood}</option>
            ))}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={addScene} disabled={adding || busyIdx !== null}>
          <Plus className="h-4 w-4 mr-1" />
          {adding ? 'Generating…' : 'Add scene'}
        </Button>
        <div className="flex-1" />
        <p className="text-[11px] text-muted-foreground self-center">
          Changes save automatically. Approving publishes accepted slides to <code>arthaus.mockups</code>.
          {saving ? ' Saving…' : ''}
        </p>
      </div>
    </div>
  )
}

function IconBtn({ children, title, onClick, disabled }: { children: React.ReactNode; title: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center h-7 min-w-7 px-1 rounded border bg-background hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  )
}
